<template>
  <div class="mb-3">
    <div class="form-inline">
      <div class="form-group mr-1 flex-grow-1">
        <b-form-input
          class="w-100"
          v-model="query"
          @keyup.enter="fetch"
          :placeholder="placeholder"
          ref="search"
        ></b-form-input>
      </div>
      <div class="form-group">
        <b-button v-if="updateSearch" type="submit" variant="success" @click="fetch">
          Update <b-icon icon="arrow-clockwise"></b-icon>
        </b-button>
        <b-button type="submit" variant="primary" @click="fetch" v-else>
          Search <b-icon icon="search"></b-icon>
        </b-button>
      </div>
      <div class="form-group ml-1" v-if="this.filters.length">
        <b-button type="submit" variant="secondary" @click="toggleFilters">
          <span>
            {{ filterBtn }} <b-icon icon="chevron-down" v-if="!filtersOpen"></b-icon
            ><b-icon icon="chevron-up" v-else></b-icon
          ></span>
        </b-button>
      </div>
      <div class="form-group ml-1">
        <b-button type="submit" variant="secondary" v-if="existingFilters || query.length > 1" @click="resetSearch">
          Reset
        </b-button>
      </div>
    </div>
    <div class="position-relative" v-show="filtersOpen">
      <div class="card bg-light w-100 position-absolute mt-3" style="z-index: 999">
        <div class="card-body">
          <div class="row row-cols-1 row-cols-md-4">
            <div class="col mb-3" v-for="(filter, index) in filters" :key="`filter_${index}`">
              <b-form-checkbox
                v-if="filter.type === 'checkbox'"
                v-model="selectedFilters[filter.field]"
                :value="filter.value"
                :unchecked-value="null"
                @change="handleChange"
              >
                {{ filter.title }}
              </b-form-checkbox>
              <span v-if="filter.type === 'select'">
                <label class="label">{{ filter.title }}</label>
                <v-select
                  v-model="selectedFilters[filter.field]"
                  label="label"
                  placeholder="Select an option"
                  :options="filter.options"
                  @input="handleChange"
                />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import autocomplete from 'autocompleter';

export default {
  props: {
    filters: {
      type: Array,
      default: () => [],
    },
    path: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: 'Type to search',
    },
    useAutocomplete: {
      type: Boolean,
      default: false,
    },
    additionalParams: {
      type: Object,
      required: false,
      default: () => new Object(),
    },
  },

  data() {
    return {
      query: '',
      filtersOpen: false,
      updateSearch: false,
      selectedFilters: {},
    };
  },

  watch: {
    query(newVal) {
      if (newVal.length < 1) {
        this.$emit('search-results', [], false);
      }
      this.$emit('query-updated', newVal);
    },
  },

  mounted() {
    if (this.useAutocomplete) {
      const { onSelect, path } = this;
      autocomplete({
        input: this.$refs.search.$el,
        minLength: 2,
        disableAutoSelect: true,
        emptyMsg: 'No cards found with that search term',
        fetch(query, update) {
          const q = encodeURIComponent(query);
          const params = { ...this.additionalParams, q };
          axios
            .get(`${path}`, {
              params,
            })
            .then((response) => {
              const suggestions = response.data.map((result) => ({ label: result.title, value: result.id }));
              update(suggestions);
            });
        },
        onSelect,
        debounceWaitMs: 200,
      });
    }
  },

  computed: {
    existingFilters() {
      return Object.values(this.selectedFilters).filter((f) => f !== null).length > 0;
    },

    filterBtn() {
      const filterLength = Object.values(this.selectedFilters).filter((f) => f !== null).length;
      return filterLength ? `${filterLength} Filter${filterLength === 1 ? '' : 's'}` : 'Filter';
    },
  },

  methods: {
    closeFilters() {
      this.filtersOpen = false;
    },

    fetch() {
      if (!this.existingFilters && this.query.length < 2) {
        this.$toast.open({
          type: 'warning',
          message: 'Enter 2 or more characters or select filters',
        });
        return;
      }
      const params = { ...this.additionalParams, q: this.query, filters: this.selectedFilters };
      axios
        .get(`${this.path}`, {
          params,
        })
        .then((response) => {
          this.$emit('search-results', response.data, response.data.length === 0);
          this.updateSearch = false;
          this.filtersOpen = false;
        });
    },

    handleChange() {
      this.updateSearch = this.existingFilters;
    },

    onSelect(item) {
      this.query = item.label;
      this.filtersOpen = false;
      this.fetch();
    },

    resetSearch() {
      this.query = '';
      this.selectedFilters = {};
      this.updateSearch = false;
      this.filtersOpen = false;
      this.$emit('search-results', [], false);
    },

    toggleFilters() {
      this.filtersOpen = !this.filtersOpen;
    },
  },
};
</script>

<style lang="scss">
.autocomplete {
  z-index: 9999;
}
</style>
